import { ReactNode } from 'react';
import type { EntityType } from '@/types/api';

export interface EntityColumnConfig {
  key: string;
  header: string;
  sortable: boolean;
  render?: (item: any) => ReactNode;
}

// Format helpers
const formatDate = (val: string | undefined | null) =>
  val ? new Date(val).toLocaleDateString() : '-';
const formatBool = (val: boolean | undefined | null) =>
  val ? 'Yes' : 'No';
const formatNumber = (val: number | undefined | null) =>
  val != null ? String(val) : '-';
const formatRate = (val: number | undefined | null) =>
  val != null ? `${val}%` : '-';

const syncStatusStyles: Record<string, { bg: string; text: string; label: string }> = {
  SYNCED:       { bg: '#dcfce7', text: '#166534', label: 'Synced' },
  PENDING_SYNC: { bg: '#fef9c3', text: '#854d0e', label: 'Pending' },
  NOT_SYNCED:   { bg: '#f3f4f6', text: '#6b7280', label: 'Not Synced' },
  CONFLICT:     { bg: '#fee2e2', text: '#991b1b', label: 'Conflict' },
};

const renderSyncStatus = (item: any): ReactNode => {
  const val = item.syncStatus as string | undefined | null;
  if (!val) return '-';
  const style = syncStatusStyles[val] || { bg: '#f3f4f6', text: '#6b7280', label: val };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: 600,
      backgroundColor: style.bg,
      color: style.text,
    }}>
      {style.label}
    </span>
  );
};

export const entityColumnsConfig: Record<EntityType, {
  all: EntityColumnConfig[];
  defaults: string[];
  label: string;
  // Explicit singular form for modal titles/messages (e.g. "Edit Menu Category") —
  // naive label.replace(/s$/, '') mangles words like "Categories" or "Taxes".
  singularLabel: string;
}> = {
  // ─── Menu Categories ───────────────────────────────────────────────
  menuCategories: {
    label: 'Menu Categories',
    singularLabel: 'Menu Category',
    defaults: ['name', 'isVisible', 'isBeverage', 'sortOrder', 'syncStatus'],
    all: [
      { key: 'id', header: 'ID', sortable: true },
      { key: 'posCategoryId', header: 'POS ID', sortable: true },
      { key: 'uuid', header: 'UUID', sortable: false },
      { key: 'name', header: 'Name', sortable: true },
      { key: 'isVisible', header: 'Visible', sortable: true, render: (item) => formatBool(item.isVisible) },
      { key: 'isBeverage', header: 'Beverage', sortable: true, render: (item) => formatBool(item.isBeverage) },
      { key: 'isMenuGroupShortcut', header: 'Group Shortcut', sortable: true, render: (item) => formatBool(item.isMenuGroupShortcut) },
      { key: 'sortOrder', header: 'Sort Order', sortable: true, render: (item) => formatNumber(item.sortOrder) },
      { key: 'buttonColorCode', header: 'Button Color', sortable: false, render: (item) => formatNumber(item.buttonColorCode) },
      { key: 'textColorCode', header: 'Text Color', sortable: false, render: (item) => formatNumber(item.textColorCode) },
      { key: 'syncStatus', header: 'Sync Status', sortable: true, render: renderSyncStatus },
      { key: 'createdDate', header: 'Created', sortable: true, render: (item) => formatDate(item.createdDate) },
      { key: 'modifiedDate', header: 'Modified', sortable: true, render: (item) => formatDate(item.modifiedDate) },
    ],
  },

  // ─── Menu Groups ───────────────────────────────────────────────────
  menuGroups: {
    label: 'Menu Groups',
    singularLabel: 'Menu Group',
    defaults: ['name', 'categoryName', 'isVisible', 'sortOrder', 'syncStatus'],
    all: [
      { key: 'id', header: 'ID', sortable: true },
      { key: 'posGroupId', header: 'POS ID', sortable: true },
      { key: 'uuid', header: 'UUID', sortable: false },
      { key: 'name', header: 'Name', sortable: true },
      { key: 'description', header: 'Description', sortable: false },
      { key: 'categoryId', header: 'Category ID', sortable: true, render: (item) => formatNumber(item.categoryId) },
      { key: 'categoryName', header: 'Category', sortable: true },
      { key: 'isVisible', header: 'Visible', sortable: true, render: (item) => formatBool(item.isVisible) },
      { key: 'isVisibleOnSite', header: 'Visible on Site', sortable: true, render: (item) => formatBool(item.isVisibleOnSite) },
      { key: 'isEnforceInstructions', header: 'Enforce Instr.', sortable: true, render: (item) => formatBool(item.isEnforceInstructions) },
      { key: 'isOnlyManager', header: 'Only Manager', sortable: true, render: (item) => formatBool(item.isOnlyManager) },
      { key: 'isStaffMeal', header: 'Staff Meal', sortable: true, render: (item) => formatBool(item.isStaffMeal) },
      { key: 'isShowNotification', header: 'Show Notif.', sortable: true, render: (item) => formatBool(item.isShowNotification) },
      { key: 'notification', header: 'Notification', sortable: false },
      { key: 'shortcutCategoryId', header: 'Shortcut Cat. ID', sortable: true, render: (item) => formatNumber(item.shortcutCategoryId) },
      { key: 'kitchenDisplayOrderId', header: 'Kitchen Display', sortable: true, render: (item) => formatNumber(item.kitchenDisplayOrderId) },
      { key: 'accountSalesAreaId', header: 'Sales Area', sortable: true },
      { key: 'sortOrder', header: 'Sort Order', sortable: true, render: (item) => formatNumber(item.sortOrder) },
      { key: 'buttonColorCode', header: 'Button Color', sortable: false, render: (item) => formatNumber(item.buttonColorCode) },
      { key: 'textColorCode', header: 'Text Color', sortable: false, render: (item) => formatNumber(item.textColorCode) },
      { key: 'imageUrl', header: 'Image URL', sortable: false },
      { key: 'syncStatus', header: 'Sync Status', sortable: true, render: renderSyncStatus },
      { key: 'createdDate', header: 'Created', sortable: true, render: (item) => formatDate(item.createdDate) },
      { key: 'modifiedDate', header: 'Modified', sortable: true, render: (item) => formatDate(item.modifiedDate) },
    ],
  },

  // ─── Menu Items ────────────────────────────────────────────────────
  menuItems: {
    label: 'Menu Items',
    singularLabel: 'Menu Item',
    defaults: ['name', 'price', 'groupName', 'visible', 'sortOrder', 'syncStatus'],
    all: [
      { key: 'id', header: 'ID', sortable: true },
      { key: 'posMenuItemId', header: 'POS ID', sortable: true },
      { key: 'uuid', header: 'UUID', sortable: false },
      { key: 'name', header: 'Name', sortable: true },
      { key: 'description', header: 'Description', sortable: false },
      { key: 'price', header: 'Price', sortable: true, render: (item) => formatNumber(item.price) },
      { key: 'groupId', header: 'Group ID', sortable: true, render: (item) => formatNumber(item.groupId) },
      { key: 'groupName', header: 'Group', sortable: true },
      { key: 'visible', header: 'Visible', sortable: true, render: (item) => formatBool(item.visible) },
      { key: 'sortOrder', header: 'Sort Order', sortable: true, render: (item) => formatNumber(item.sortOrder) },
      { key: 'imageUrl', header: 'Image URL', sortable: false },
      { key: 'barcode', header: 'Barcode', sortable: false },
      { key: 'unitName', header: 'Unit', sortable: false },
      { key: 'buyPrice', header: 'Buy Price', sortable: true, render: (item) => formatNumber(item.buyPrice) },
      { key: 'stockAmount', header: 'Stock', sortable: true, render: (item) => formatNumber(item.stockAmount) },
      { key: 'manageStock', header: 'Manage Stock', sortable: true, render: (item) => formatBool(item.manageStock) },
      { key: 'disableWhenStockAmountIsZero', header: 'Disable @ 0 Stock', sortable: true, render: (item) => formatBool(item.disableWhenStockAmountIsZero) },
      { key: 'webPrice', header: 'Web Price', sortable: true, render: (item) => formatNumber(item.webPrice) },
      { key: 'discountRate', header: 'Discount %', sortable: true, render: (item) => formatRate(item.discountRate) },
      { key: 'minQuantityGroup', header: 'Min Qty', sortable: false, render: (item) => formatNumber(item.minQuantityGroup) },
      { key: 'maxQuantityGroup', header: 'Max Qty', sortable: false, render: (item) => formatNumber(item.maxQuantityGroup) },
      { key: 'buttonColorCode', header: 'Btn Color', sortable: false, render: (item) => formatNumber(item.buttonColorCode) },
      { key: 'textColorCode', header: 'Text Color', sortable: false, render: (item) => formatNumber(item.textColorCode) },
      { key: 'enforceInstructions', header: 'Enforce Instr.', sortable: false, render: (item) => formatBool(item.enforceInstructions) },
      { key: 'visibleOnSite', header: 'Online', sortable: false, render: (item) => formatBool(item.visibleOnSite) },
      { key: 'hideOnKitchen', header: 'Hide Kitchen', sortable: false, render: (item) => formatBool(item.hideOnKitchen) },
      { key: 'hideOnReceipt', header: 'Hide Receipt', sortable: false, render: (item) => formatBool(item.hideOnReceipt) },
      { key: 'alcoholicDrink', header: 'Alcoholic', sortable: false, render: (item) => formatBool(item.alcoholicDrink) },
      { key: 'openItem', header: 'Open Item', sortable: false, render: (item) => formatBool(item.openItem) },
      { key: 'saleAreaId', header: 'Sale Area', sortable: false },
      { key: 'syncStatus', header: 'Sync Status', sortable: true, render: renderSyncStatus },
      { key: 'createdDate', header: 'Created', sortable: true, render: (item) => formatDate(item.createdDate) },
      { key: 'modifiedDate', header: 'Modified', sortable: true, render: (item) => formatDate(item.modifiedDate) },
    ],
  },

  // ─── Taxes ─────────────────────────────────────────────────────────
  taxes: {
    label: 'Taxes',
    singularLabel: 'Tax',
    defaults: ['name', 'rate', 'syncStatus'],
    all: [
      { key: 'id', header: 'ID', sortable: true },
      { key: 'posTaxId', header: 'POS ID', sortable: true },
      { key: 'uuid', header: 'UUID', sortable: false },
      { key: 'name', header: 'Name', sortable: true },
      { key: 'rate', header: 'Rate', sortable: true, render: (item) => formatRate(item.rate) },
      { key: 'syncStatus', header: 'Sync Status', sortable: true, render: renderSyncStatus },
      { key: 'createdDate', header: 'Created', sortable: true, render: (item) => formatDate(item.createdDate) },
      { key: 'modifiedDate', header: 'Modified', sortable: true, render: (item) => formatDate(item.modifiedDate) },
    ],
  },

  // ─── Discounts ─────────────────────────────────────────────────────
  discounts: {
    label: 'Discounts',
    singularLabel: 'Discount',
    defaults: ['name', 'type', 'value', 'isEnabled', 'isAutoApply', 'syncStatus'],
    all: [
      { key: 'id', header: 'ID', sortable: true },
      { key: 'posDiscountId', header: 'POS ID', sortable: true },
      { key: 'uuid', header: 'UUID', sortable: false },
      { key: 'name', header: 'Name', sortable: true },
      { key: 'type', header: 'Type', sortable: true },
      { key: 'barcode', header: 'Barcode', sortable: true },
      { key: 'qualificationType', header: 'Qualification', sortable: true },
      { key: 'isEnabled', header: 'Enabled', sortable: true, render: (item) => formatBool(item.isEnabled) },
      { key: 'isAutoApply', header: 'Auto Apply', sortable: true, render: (item) => formatBool(item.isAutoApply) },
      { key: 'isAutoApplyAfterSelection', header: 'Auto After Selection', sortable: true, render: (item) => formatBool(item.isAutoApplyAfterSelection) },
      { key: 'isNeverExpire', header: 'Never Expire', sortable: true, render: (item) => formatBool(item.isNeverExpire) },
{ key: 'isOnlyFood', header: 'Only Food', sortable: true, render: (item) => formatBool(item.isOnlyFood) },
      { key: 'isOnlyDrinks', header: 'Only Drinks', sortable: true, render: (item) => formatBool(item.isOnlyDrinks) },
      { key: 'isWebsiteOnly', header: 'Website Only', sortable: true, render: (item) => formatBool(item.isWebsiteOnly) },
      { key: 'isApplyToDeliveryCharge', header: 'Delivery Charge', sortable: true, render: (item) => formatBool(item.isApplyToDeliveryCharge) },
      { key: 'isApplyOnLast', header: 'Apply On Last', sortable: true, render: (item) => formatBool(item.isApplyOnLast) },
      { key: 'minimumBuy', header: 'Min. Order Value', sortable: true, render: (item) => formatNumber(item.minimumBuy) },
      { key: 'value', header: 'Value', sortable: true, render: (item) => formatNumber(item.value) },
      { key: 'minQuantity', header: 'Min Quantity', sortable: true, render: (item) => formatNumber(item.minQuantity) },
      { key: 'maxValuePerGuest', header: 'Max Per Guest', sortable: true, render: (item) => formatNumber(item.maxValuePerGuest) },
      { key: 'expiryDate', header: 'Expiry Date', sortable: true, render: (item) => formatDate(item.expiryDate) },
      { key: 'syncStatus', header: 'Sync Status', sortable: true, render: renderSyncStatus },
      { key: 'createdDate', header: 'Created', sortable: true, render: (item) => formatDate(item.createdDate) },
      { key: 'modifiedDate', header: 'Modified', sortable: true, render: (item) => formatDate(item.modifiedDate) },
    ],
  },

  // ─── Order Types ───────────────────────────────────────────────────
  orderTypes: {
    label: 'Order Types',
    singularLabel: 'Order Type',
    defaults: ['name', 'isDelivery', 'isPrepaid', 'isKiosk', 'sortOrder', 'syncStatus'],
    all: [
      { key: 'id', header: 'ID', sortable: true },
      { key: 'posOrderTypeId', header: 'POS ID', sortable: true },
      { key: 'uuid', header: 'UUID', sortable: false },
      { key: 'name', header: 'Name', sortable: true },
      { key: 'branchName', header: 'Branch Name', sortable: true },
      { key: 'branchPhone', header: 'Branch Phone', sortable: false },
      { key: 'isEnabled', header: 'Enabled', sortable: true, render: (item) => formatBool(item.isEnabled) },
      { key: 'isPrepaid', header: 'Prepaid', sortable: true, render: (item) => formatBool(item.isPrepaid) },
      { key: 'isDelivery', header: 'Delivery', sortable: true, render: (item) => formatBool(item.isDelivery) },
      { key: 'isKiosk', header: 'Kiosk', sortable: true, render: (item) => formatBool(item.isKiosk) },
      { key: 'isBarTab', header: 'Bar Tab', sortable: true, render: (item) => formatBool(item.isBarTab) },
      { key: 'sortOrder', header: 'Sort Order', sortable: true, render: (item) => formatNumber(item.sortOrder) },
      { key: 'colorCode', header: 'Color Code', sortable: false, render: (item) => formatNumber(item.colorCode) },
      { key: 'textColor', header: 'Text Color', sortable: false, render: (item) => formatNumber(item.textColor) },
      { key: 'isShowTableSelection', header: 'Table Selection', sortable: true, render: (item) => formatBool(item.isShowTableSelection) },
      { key: 'isShouldPrintToKitchen', header: 'Print Kitchen', sortable: true, render: (item) => formatBool(item.isShouldPrintToKitchen) },
      { key: 'isCloseOnPaid', header: 'Close On Paid', sortable: true, render: (item) => formatBool(item.isCloseOnPaid) },
      { key: 'isRequiredCustomerData', header: 'Req. Customer', sortable: true, render: (item) => formatBool(item.isRequiredCustomerData) },
      { key: 'isHasForHereAndToGo', header: 'Here/To Go', sortable: true, render: (item) => formatBool(item.isHasForHereAndToGo) },
      { key: 'isStaffMeal', header: 'Staff Meal', sortable: true, render: (item) => formatBool(item.isStaffMeal) },
      { key: 'isShowInLoginScreen', header: 'Login Screen', sortable: true, render: (item) => formatBool(item.isShowInLoginScreen) },
      { key: 'isUseKitchenDisplayOrder', header: 'Use KDO', sortable: true, render: (item) => formatBool(item.isUseKitchenDisplayOrder) },
      { key: 'bookingType', header: 'Booking Type', sortable: true },
      { key: 'isPrintShortReceipt', header: 'Short Receipt', sortable: true, render: (item) => formatBool(item.isPrintShortReceipt) },
      { key: 'isPreapproved', header: 'Preapproved', sortable: true, render: (item) => formatBool(item.isPreapproved) },
      { key: 'footerMessage', header: 'Footer', sortable: false },
      { key: 'minuteAsap', header: 'Min ASAP', sortable: true, render: (item) => formatNumber(item.minuteAsap) },
      { key: 'isEnforceDeliveryCharge', header: 'Enforce Delivery', sortable: true, render: (item) => formatBool(item.isEnforceDeliveryCharge) },
      { key: 'isWasted', header: 'Wasted', sortable: true, render: (item) => formatBool(item.isWasted) },
      { key: 'syncStatus', header: 'Sync Status', sortable: true, render: renderSyncStatus },
      { key: 'createdDate', header: 'Created', sortable: true, render: (item) => formatDate(item.createdDate) },
      { key: 'modifiedDate', header: 'Modified', sortable: true, render: (item) => formatDate(item.modifiedDate) },
    ],
  },

  // ─── Custom Payments ───────────────────────────────────────────────
  customPayments: {
    label: 'Custom Payments',
    singularLabel: 'Custom Payment',
    defaults: ['name', 'isVoucher', 'isOpenDrawerOnPayment', 'sortOrder', 'syncStatus'],
    all: [
      { key: 'id', header: 'ID', sortable: true },
      { key: 'posCustomPaymentId', header: 'POS ID', sortable: true },
      { key: 'uuid', header: 'UUID', sortable: false },
      { key: 'name', header: 'Name', sortable: true },
      { key: 'isRequiredRefNumber', header: 'Req. Ref Number', sortable: true, render: (item) => formatBool(item.isRequiredRefNumber) },
      { key: 'isUseForPayByVoucher', header: 'Pay By Voucher', sortable: true, render: (item) => formatBool(item.isUseForPayByVoucher) },
      { key: 'isUniqueReferenceNumber', header: 'Unique Ref', sortable: true, render: (item) => formatBool(item.isUniqueReferenceNumber) },
      { key: 'isUseFrontButton', header: 'Front Button', sortable: true, render: (item) => formatBool(item.isUseFrontButton) },
      { key: 'isOpenDrawerOnPayment', header: 'Open Drawer', sortable: true, render: (item) => formatBool(item.isOpenDrawerOnPayment) },
      { key: 'isVoucher', header: 'Voucher', sortable: true, render: (item) => formatBool(item.isVoucher) },
      { key: 'refNumberFieldName', header: 'Ref Field Name', sortable: false },
      { key: 'cardType', header: 'Card Type', sortable: true },
      { key: 'sortOrder', header: 'Sort Order', sortable: true, render: (item) => formatNumber(item.sortOrder) },
      { key: 'buttonColorCode', header: 'Button Color', sortable: false, render: (item) => formatNumber(item.buttonColorCode) },
      { key: 'textColorCode', header: 'Text Color', sortable: false, render: (item) => formatNumber(item.textColorCode) },
      { key: 'syncStatus', header: 'Sync Status', sortable: true, render: renderSyncStatus },
      { key: 'createdDate', header: 'Created', sortable: true, render: (item) => formatDate(item.createdDate) },
      { key: 'modifiedDate', header: 'Modified', sortable: true, render: (item) => formatDate(item.modifiedDate) },
    ],
  },

  // ─── Cooking Instructions ──────────────────────────────────────────
  cookingInstructions: {
    label: 'Cooking Instructions',
    singularLabel: 'Cooking Instruction',
    defaults: ['description', 'isGeneral', 'isFoodOnly', 'isDrinkOnly', 'sortOrder', 'syncStatus'],
    all: [
      { key: 'id', header: 'ID', sortable: true },
      { key: 'posInstructionId', header: 'POS ID', sortable: true },
      { key: 'uuid', header: 'UUID', sortable: false },
      { key: 'description', header: 'Description', sortable: true },
      { key: 'isGeneral', header: 'General', sortable: true, render: (item) => formatBool(item.isGeneral) },
      { key: 'isFoodOnly', header: 'Food Only', sortable: true, render: (item) => formatBool(item.isFoodOnly) },
      { key: 'isDrinkOnly', header: 'Drink Only', sortable: true, render: (item) => formatBool(item.isDrinkOnly) },
      { key: 'sortOrder', header: 'Sort Order', sortable: true, render: (item) => formatNumber(item.sortOrder) },
      { key: 'buttonColorCode', header: 'Button Color', sortable: false, render: (item) => formatNumber(item.buttonColorCode) },
      { key: 'textColorCode', header: 'Text Color', sortable: false, render: (item) => formatNumber(item.textColorCode) },
      { key: 'syncStatus', header: 'Sync Status', sortable: true, render: renderSyncStatus },
      { key: 'createdDate', header: 'Created', sortable: true, render: (item) => formatDate(item.createdDate) },
      { key: 'modifiedDate', header: 'Modified', sortable: true, render: (item) => formatDate(item.modifiedDate) },
    ],
  },

  // ─── Kitchen Display Orders ────────────────────────────────────────
  kitchenDisplayOrders: {
    label: 'Kitchen Display Orders',
    singularLabel: 'Kitchen Display Order',
    defaults: ['name', 'isEnabled', 'isDefaultDisplay', 'orderNo', 'syncStatus'],
    all: [
      { key: 'id', header: 'ID', sortable: true },
      { key: 'posKitchenDisplayOrderId', header: 'POS ID', sortable: true },
      { key: 'uuid', header: 'UUID', sortable: false },
      { key: 'name', header: 'Name', sortable: true },
      { key: 'isEnabled', header: 'Enabled', sortable: true, render: (item) => formatBool(item.isEnabled) },
      { key: 'isDefaultDisplay', header: 'Default Display', sortable: true, render: (item) => formatBool(item.isDefaultDisplay) },
      { key: 'isWhenReady', header: 'When Ready', sortable: true, render: (item) => formatBool(item.isWhenReady) },
      { key: 'isAllTogether', header: 'All Together', sortable: true, render: (item) => formatBool(item.isAllTogether) },
      { key: 'orderNo', header: 'Order No', sortable: true, render: (item) => formatNumber(item.orderNo) },
      { key: 'colorOrder', header: 'Color Order', sortable: false, render: (item) => formatNumber(item.colorOrder) },
      { key: 'buttonColorCode', header: 'Button Color', sortable: false, render: (item) => formatNumber(item.buttonColorCode) },
      { key: 'textColorCode', header: 'Text Color', sortable: false, render: (item) => formatNumber(item.textColorCode) },
      { key: 'menuGroupIds', header: 'Menu Group IDs', sortable: false },
      { key: 'syncStatus', header: 'Sync Status', sortable: true, render: renderSyncStatus },
      { key: 'createdDate', header: 'Created', sortable: true, render: (item) => formatDate(item.createdDate) },
      { key: 'modifiedDate', header: 'Modified', sortable: true, render: (item) => formatDate(item.modifiedDate) },
    ],
  },

  // ─── Printer Groups ────────────────────────────────────────────────
  printerGroups: {
    label: 'Printer Groups',
    singularLabel: 'Printer Group',
    defaults: ['name', 'type', 'isDefault', 'syncStatus'],
    all: [
      { key: 'id', header: 'ID', sortable: true },
      { key: 'posPrinterGroupId', header: 'POS ID', sortable: true },
      { key: 'uuid', header: 'UUID', sortable: false },
      { key: 'name', header: 'Name', sortable: true },
      { key: 'isDefault', header: 'Default', sortable: true, render: (item) => formatBool(item.isDefault) },
      { key: 'type', header: 'Type', sortable: true, render: (item) => ({ '1': 'Kitchen', '2': 'Bar', '3': 'Receipt' }[item.type as string] || item.type || '-') },
      { key: 'syncStatus', header: 'Sync Status', sortable: true, render: renderSyncStatus },
      { key: 'createdDate', header: 'Created', sortable: true, render: (item) => formatDate(item.createdDate) },
      { key: 'modifiedDate', header: 'Modified', sortable: true, render: (item) => formatDate(item.modifiedDate) },
    ],
  },

  // ─── Account Sales Areas ───────────────────────────────────────────
  accountSalesAreas: {
    label: 'Account Sales Areas',
    singularLabel: 'Account Sales Area',
    defaults: ['name', 'isEnabled', 'sortOrder', 'syncStatus'],
    all: [
      { key: 'id', header: 'ID', sortable: true },
      { key: 'posAccountSalesAreaId', header: 'POS ID', sortable: true },
      { key: 'uuid', header: 'UUID', sortable: false },
      { key: 'name', header: 'Name', sortable: true },
      { key: 'isEnabled', header: 'Enabled', sortable: true, render: (item) => formatBool(item.isEnabled) },
      { key: 'sortOrder', header: 'Sort Order', sortable: true, render: (item) => formatNumber(item.sortOrder) },
      { key: 'menuGroupIds', header: 'Menu Group IDs', sortable: false },
      { key: 'syncStatus', header: 'Sync Status', sortable: true, render: renderSyncStatus },
      { key: 'createdDate', header: 'Created', sortable: true, render: (item) => formatDate(item.createdDate) },
      { key: 'modifiedDate', header: 'Modified', sortable: true, render: (item) => formatDate(item.modifiedDate) },
    ],
  },

  // ─── Ask Next Reasons ──────────────────────────────────────────────
  askNextReasons: {
    label: 'Ask Next Reasons',
    singularLabel: 'Ask Next Reason',
    defaults: ['name', 'isEnabled', 'type', 'sortOrder', 'syncStatus'],
    all: [
      { key: 'id', header: 'ID', sortable: true },
      { key: 'posAskNextReasonId', header: 'POS ID', sortable: true },
      { key: 'uuid', header: 'UUID', sortable: false },
      { key: 'name', header: 'Name', sortable: true },
      { key: 'isEnabled', header: 'Enabled', sortable: true, render: (item) => formatBool(item.isEnabled) },
      { key: 'sortOrder', header: 'Sort Order', sortable: true, render: (item) => formatNumber(item.sortOrder) },
      { key: 'colorOrder', header: 'Color Order', sortable: false, render: (item) => formatNumber(item.colorOrder) },
      { key: 'buttonColorCode', header: 'Button Color', sortable: false, render: (item) => formatNumber(item.buttonColorCode) },
      { key: 'textColorCode', header: 'Text Color', sortable: false, render: (item) => formatNumber(item.textColorCode) },
      { key: 'type', header: 'Type', sortable: true },
      { key: 'printerGroupId', header: 'Printer Group ID', sortable: true, render: (item) => formatNumber(item.printerGroupId) },
      { key: 'printerGroupName', header: 'Printer Group', sortable: true },
      { key: 'syncStatus', header: 'Sync Status', sortable: true, render: renderSyncStatus },
      { key: 'createdDate', header: 'Created', sortable: true, render: (item) => formatDate(item.createdDate) },
      { key: 'modifiedDate', header: 'Modified', sortable: true, render: (item) => formatDate(item.modifiedDate) },
    ],
  },

  // ─── Languages ─────────────────────────────────────────────────────
  languages: {
    label: 'Languages',
    singularLabel: 'Language',
    defaults: ['name', 'isEnabled', 'syncStatus'],
    all: [
      { key: 'id', header: 'ID', sortable: true },
      { key: 'posLanguageId', header: 'POS ID', sortable: true },
      { key: 'uuid', header: 'UUID', sortable: false },
      { key: 'name', header: 'Name', sortable: true },
      { key: 'isEnabled', header: 'Enabled', sortable: true, render: (item) => formatBool(item.isEnabled) },
      { key: 'syncStatus', header: 'Sync Status', sortable: true, render: renderSyncStatus },
      { key: 'createdDate', header: 'Created', sortable: true, render: (item) => formatDate(item.createdDate) },
      { key: 'modifiedDate', header: 'Modified', sortable: true, render: (item) => formatDate(item.modifiedDate) },
    ],
  },
  // ─── Shifts ─────────────────────────────────────────────────────
  shifts: {
    label: 'Shifts',
    singularLabel: 'Shift',
    defaults: ['name', 'startTime', 'endTime', 'daysOfWeek', 'syncStatus'],
    all: [
      { key: 'id', header: 'ID', sortable: true },
      { key: 'posShiftId', header: 'POS ID', sortable: true },
      { key: 'uuid', header: 'UUID', sortable: false },
      { key: 'name', header: 'Name', sortable: true },
      { key: 'startTime', header: 'Start Time', sortable: true },
      { key: 'endTime', header: 'End Time', sortable: true },
      { key: 'shiftLength', header: 'Length', sortable: false, render: (item) => formatNumber(item.shiftLength) },
      { key: 'daysOfWeek', header: 'Days', sortable: false },
      { key: 'startDate', header: 'Start Date', sortable: true, render: (item) => formatDate(item.startDate) },
      { key: 'endDate', header: 'End Date', sortable: true, render: (item) => formatDate(item.endDate) },
      { key: 'syncStatus', header: 'Sync Status', sortable: true, render: renderSyncStatus },
      { key: 'createdDate', header: 'Created', sortable: true, render: (item) => formatDate(item.createdDate) },
      { key: 'modifiedDate', header: 'Modified', sortable: true, render: (item) => formatDate(item.modifiedDate) },
    ],
  },
};

// Ordered list for the drawer
export const entityDrawerOrder: EntityType[] = [
  'menuCategories',
  'menuGroups',
  'menuItems',
  'shifts',
  'taxes',
  'discounts',
  'orderTypes',
  'customPayments',
  'cookingInstructions',
  'kitchenDisplayOrders',
  'printerGroups',
  'accountSalesAreas',
  'askNextReasons',
  'languages',
];
